import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Eye, EyeOff } from 'lucide-react';

import { Switch } from '@/components/ui/switch';
import { ColumnConfig } from '../types';

interface SortableColumnItemProps {
  column: ColumnConfig;
  onToggle: (columnId: string) => void;
}

export function SortableColumnItem({ column, onToggle }: SortableColumnItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 p-3 bg-white border border-border rounded-xl mb-3 transition-all ${
        isDragging ? 'z-50 shadow-xl border-primary/20 bg-muted' : 'shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md'
      }`}>
      <div className="flex items-center gap-3 flex-1">
        <button
          className="cursor-grab active:cursor-grabbing text-neutral-alpha-400 hover:text-muted-foreground p-1"
          {...attributes}
          {...listeners}>
          <GripVertical className="h-5 w-5" />
        </button>

        <div className="flex items-center justify-center w-8 text-muted-foreground">
          {column.visible ? <Eye className="h-5 w-5" /> : <EyeOff className="h-4 w-4 opacity-50" />}
        </div>

        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-[15px] font-semibold text-foreground truncate">{column.label}</span>
          {column.description && (
            <span className="text-[13px] text-muted-foreground truncate mt-0.5">{column.description}</span>
          )}
        </div>
      </div>

      <div className="pl-2">
        <Switch checked={column.visible} onCheckedChange={() => onToggle(column.id)} />
      </div>
    </div>
  );
}
